import { createContext, type ReactNode, useContext, useMemo } from "react";
import type { AwayInfo } from "@/shared/presence/away-label";

/**
 * Who is out, for every surface that draws a person.
 *
 * ## Why a context and not a hook per surface
 *
 * The badge is meant to appear on roughly thirty surfaces, and a directory
 * page draws sixty avatars. A hook inside the avatar would be sixty
 * subscriptions to one query on one page. One request per workspace, one map,
 * and every avatar does a `Map.get`.
 *
 * ## Why this file fetches nothing
 *
 * `out-today` is the leave module's route, and `shared/` may not import a
 * feature (G3). So this holds the shape and the lookup, and the workspace
 * layout, which is allowed to reach into features, does the fetching and feeds
 * it in. Same seam the relationship board uses for its reputation slot.
 *
 * ## What absence means here
 *
 * The map holds ONLY people who are out, so a missing entry means "not out",
 * never "unknown". That distinction is the whole privacy stance: attendance
 * has a `not_marked` status for a day nobody closed, and rendering that as a
 * grey "unknown" dot draws suspicion onto somebody whose manager is simply
 * behind on paperwork. Nothing in this path can express it, on purpose.
 */
export interface PresenceValue {
  /**
   * Keyed by membership, not user.
   *
   * `out-today` returns membership ids, and so do the payloads that draw
   * people in tables and directories. Surfaces that hold only a `userId`
   * (todos, kudos, feedback) get no badge, which is the honest boundary
   * rather than a silent join through a directory page that may not be
   * loaded.
   */
  awayByMembershipId: ReadonlyMap<string, AwayInfo>;
  /// The workspace-local date the map describes, `YYYY-MM-DD`. Every label is
  /// phrased relative to this rather than to the reader's clock, so a browser
  /// left open past midnight does not start saying "away until yesterday".
  today: string;
  /// False while the first fetch is in flight. Consumers draw nothing rather
  /// than a placeholder badge: a dot that appears and then vanishes reads as a
  /// bug, and absence is rare enough that the flicker would be the common case.
  isReady: boolean;
}

const EMPTY: PresenceValue = {
  awayByMembershipId: new Map(),
  today: "",
  isReady: false,
};

const PresenceContext = createContext<PresenceValue>(EMPTY);

/**
 * Feeds the map down. Takes data rather than fetching it, so this file stays
 * on the shared side of G3 and so tests and stories can mount an avatar with a
 * fixed absence and no query client.
 */
export function PresenceProvider({
  away,
  today,
  isReady,
  children,
}: {
  away: ReadonlyMap<string, AwayInfo>;
  today: string;
  isReady: boolean;
  children: ReactNode;
}): ReactNode {
  const value = useMemo<PresenceValue>(
    () => ({ awayByMembershipId: away, today, isReady }),
    [away, today, isReady],
  );
  return <PresenceContext.Provider value={value}>{children}</PresenceContext.Provider>;
}

/// The map itself, for a surface that needs more than one person's state.
export function usePresence(): PresenceValue {
  return useContext(PresenceContext);
}

/**
 * One person's absence, or null.
 *
 * Null covers every "draw nothing" case at once: no membership id, no
 * provider mounted, first fetch still running, or simply present. Callers get
 * one branch instead of four, which is what keeps the badge from being
 * reinvented per surface.
 */
export function useAway(membershipId: string | undefined | null): AwayInfo | null {
  const { awayByMembershipId, isReady } = usePresence();
  if (!isReady || !membershipId) return null;
  return awayByMembershipId.get(membershipId) ?? null;
}
